import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import VantaBackground from './componets/vantabackground';

const firestore = getFirestore();

const HonPropDesk = () => {
  const [activeFeature, setActiveFeature] = useState('risk');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    broker: '',
    customPropFirm: ''
  });

  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const features = {
    risk: {
      title: 'RISK MANAGEMENT',
      items: [
        'Unified Risk Calculator: Automatically calculates exact lot sizes based on account percentage risk across 20+ instruments',
        'Real-Time Drawdown Protection: Instant account monitoring with automatic trading halt at preset levels',
        'Multi-Symbol Risk Analysis: Live dashboard showing risk exposure across entire portfolio',
        'Precise Stop Loss/Take Profit: Automatic calculation accounting for broker minimums and pip values'
      ],
      icon: '🛡️'
    },
    compliance: {
      title: 'PROP FIRM COMPLIANCE',
      items: [
        'Automated Rule Enforcement: Built-in daily loss limits, drawdown monitoring, and position size controls',
        'Weekend Position Closure: Automatic position management for prop firm requirements',
        'News Trading Blocks: Smart filtering prevents trading during high-impact economic events',
        'Violation Prevention: Real-time alerts before approaching critical thresholds'
      ],
      icon: '✅'
    },
    automation: {
      title: 'PROFESSIONAL AUTOMATION',
      items: [
        'One-Click Position Management: Instant breakeven, trailing stops, and partial closure across all positions',
        'Advanced Order Types: Buy/sell limits with automatic stop and target calculation',
        'Market Condition Filters: ATR, spread, and session-based trading controls',
        'Emergency Stop System: Instant account protection with full position closure'
      ],
      icon: '⚡'
    },
    monitoring: {
      title: 'REMOTE MONITORING',
      items: [
        'Telegram Integration: Complete account control and monitoring via mobile device',
        'Real-Time Alerts: Instant notifications for trades, risk events, and account milestones',
        'Remote Commands: Execute trades, close positions, and manage account from anywhere',
        'Status Reports: Automated daily/hourly performance summaries'
      ],
      icon: '📱'
    },
    psychology: {
      title: 'TRADING PSYCHOLOGY',
      items: [
        'Consecutive Loss Breaker: Automatic cooling-off periods after preset number of losses',
        'Revenge Trade Detection: Alerts when lot sizes exceed normal parameters',
        'Performance Analytics: Win rate, profit factor, and risk-reward tracking',
        'Discipline Enforcement: Built-in safeguards against emotional trading decisions'
      ],
      icon: '🧠'
    },
    intelligence: {
      title: 'MARKET INTELLIGENCE',
      items: [
        'Smart News Filtering: Currency-specific economic calendar with impact assessment',
        'Session Optimization: Automatic trading schedule based on market sessions',
        'Volatility Analysis: Real-time ATR monitoring for optimal entry timing',
        'Correlation Management: Prevents overexposure to related currency pairs'
      ],
      icon: '📊'
    }
  };

  const techFeatures = [
    { title: 'Multi-Instrument Support', desc: 'Forex, indices, commodities, crypto, and metals' },
    { title: 'Broker Agnostic', desc: 'Works with any MT5 broker and account type' },
    { title: 'Performance Optimized', desc: 'Minimal CPU usage with smart update intervals' },
    { title: 'Professional Grade', desc: 'Enterprise-level trading management system' }
  ];

  const brokers = [
    { value: 'FTMO', label: 'FTMO' },
    { value: 'FundedNext', label: 'Funded Next' },
    { value: 'FundedPip', label: 'Funded Pip' },
    { value: 'Hantec', label: 'Hantec Trading' },
    { value: 'TenTrade', label: 'Ten Trade' },
    { value: 'Other', label: 'Other (please specify)' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.broker) {
      newErrors.broker = 'Please select a prop firm';
    } else if (formData.broker === 'Other' && !formData.customPropFirm.trim()) {
      newErrors.customPropFirm = 'Please specify your prop firm';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmissionStatus('submitting');

    try {
      const submissionData = {
        ...formData,
        timestamp: serverTimestamp(),
        status: 'pending',
        source: 'free_tools_honpropdesk',
        tool: 'HonPropDesk Manager v3.3'
      };

      const docRef = await addDoc(collection(firestore, 'FreeToolSignups'), submissionData);
      console.log('Free tool signup saved with ID:', docRef.id);

      setSubmissionStatus('success');

      setFormData({
        name: '',
        email: '',
        phone: '',
        broker: '',
        customPropFirm: ''
      });

      setTimeout(() => {
        setSubmissionStatus(null);
      }, 8000);

    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmissionStatus('error');

      setTimeout(() => {
        setSubmissionStatus(null);
      }, 5000);
    }
  };

  return (
    <>
      {/* CSS for consistent styling */}
      <style>{`
        .honpropdesk-container {
          position: relative;
          min-height: 100vh;
          width: 100%;
        }
      `}</style>

      <div className="honpropdesk-container relative w-full font-sans">
        {/* Vanta Background */}
        <div className="fixed inset-0 z-0" style={{ pointerEvents: 'none' }}>
          <VantaBackground
            color={0x1e40af}
            backgroundColor={0x000000}
            scale={1.2}
            scaleMobile={1.0}
          />
        </div>

        {/* Gradient Overlay */}
        <div className="fixed inset-0 z-[1] bg-gradient-to-b from-black/80 via-black/70 to-black/80" />

        {/* Additional overlay for better text contrast */}
        <div className="fixed inset-0 z-[2] bg-black/40" />

        {/* Back Button */}
        <Link
          to="/free-tools"
          className="fixed top-24 left-4 z-[999] glass-btn glass-btn-secondary px-3 py-2 text-sm flex items-center gap-2 hover:scale-105 transition-transform"
        >
          <span>←</span>
          <span className="hidden sm:inline">Back to Free Tools</span>
          <span className="sm:hidden">Back</span>
        </Link>

        {/* Main Content */}
        <div className="relative z-[10] min-h-full pt-32 pb-32 px-4">
          <div className="max-w-7xl mx-auto">

            {/* Hero Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 px-4 py-2 rounded-full text-white font-bold mb-6">
                <span className="text-xl">🏢</span>
                <span>PROP TRADING EXCELLENCE</span>
              </div>

              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-6 sm:mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent font-orbitron leading-tight px-4">
                Hon PropDesk Manager
              </h1>

                  <p className="text-base sm:text-lg lg:text-xl text-white font-medium mb-8 max-w-3xl mx-auto leading-relaxed text-left px-4" style={{textShadow: '0 2px 4px rgba(0,0,0,0.8)'}}>
                  The Ultimate Prop Firm Trading Management System. Advanced risk management, automated compliance,
                  and professional-grade tools designed specifically for prop traders who demand excellence.
                </p>

              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="glass-card p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400">20+</div>
                  <div className="text-sm text-gray-300 font-semibold">Instruments</div>
                </div>
                <div className="glass-card p-4 text-center">
                  <div className="text-2xl font-bold text-purple-400">24/7</div>
                  <div className="text-sm text-gray-300 font-semibold">Monitoring</div>
                </div>
                <div className="glass-card p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">100%</div>
                  <div className="text-sm text-gray-300 font-semibold">Compliant</div>
                </div>
                <div className="glass-card p-4 text-center">
                  <div className="text-2xl font-bold text-orange-400">v3.3</div>
                  <div className="text-sm text-gray-300 font-semibold">Latest</div>
                </div>
              </div>
            </motion.div>

            {/* Feature Navigation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-12"
            >
              <div className="flex flex-wrap justify-center gap-1 sm:gap-2 mb-6 sm:mb-8 px-4">
                {Object.keys(features).map((key) => (
                  <button
                    key={key}
                    onClick={() => setActiveFeature(key)}
                    className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                      activeFeature === key
                        ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                        : 'glass-btn glass-btn-secondary text-gray-300 hover:text-white'
                    }`}
                  >
                    {features[key].icon} {features[key].title.split(' ')[0]}
                  </button>
                ))}
              </div>

              {/* Active Feature Display */}
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
                className="glass-card glass-card-large"
              >
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-4 sm:mb-6 text-left">
                  {features[activeFeature].icon} {features[activeFeature].title}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {features[activeFeature].items.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3 p-3 sm:p-4 bg-white/10 rounded-lg backdrop-blur-sm border border-white/20 shadow-sm"
                    >
                      <span className="text-blue-400 text-lg sm:text-xl mt-0.5">•</span>
                      <span className="text-gray-200 text-xs sm:text-sm leading-relaxed font-medium">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            {/* Technical Advantages */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mb-16"
            >
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-left mb-6 sm:mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent px-4">
                Technical Advantages
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {techFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="glass-card text-center hover:scale-105 transition-transform"
                  >
                    <h4 className="font-bold text-blue-400 text-sm sm:text-base mb-2">{feature.title}</h4>
                    <p className="text-xs sm:text-sm text-gray-300 font-medium">{feature.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* User Interface Highlights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mb-16"
            >
              <div className="glass-card glass-card-large">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-left mb-6 sm:mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Professional Interface
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
                  <div>
                    <h4 className="text-xl font-bold text-blue-400 mb-4">🎨 Modern Design</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-3">
                        <span className="text-emerald-400">✓</span>
                        <span className="text-gray-300 font-medium">Modern Draggable Panels</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <span className="text-emerald-400">✓</span>
                        <span className="text-gray-300 font-medium">Real-Time Data Display</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <span className="text-emerald-400">✓</span>
                        <span className="text-gray-300 font-medium">Keyboard Navigation</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <span className="text-emerald-400">✓</span>
                        <span className="text-gray-300 font-medium">Visual Risk Dashboard</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-purple-400 mb-4">⚡ Performance</h4>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-3">
                        <span className="text-emerald-400">✓</span>
                        <span className="text-gray-300 font-medium">Minimal CPU Usage</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <span className="text-emerald-400">✓</span>
                        <span className="text-gray-300 font-medium">Smart Update Intervals</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <span className="text-emerald-400">✓</span>
                        <span className="text-gray-300 font-medium">Instant Response Times</span>
                      </li>
                      <li className="flex items-center gap-3">
                        <span className="text-emerald-400">✓</span>
                        <span className="text-gray-300 font-medium">Memory Optimized</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Signup Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-center"
            >
              <div className="bg-gradient-to-br from-slate-50 to-blue-50/80 backdrop-blur-lg rounded-2xl p-6 sm:p-8 shadow-2xl shadow-blue-500/10 max-w-2xl mx-auto border border-blue-200/30">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent text-left">
                  Get HonPropDesk Manager v3.3 FREE
                </h2>
                <p className="text-base sm:text-lg text-slate-700 mb-6 sm:mb-8 font-medium leading-relaxed text-left">
                  Connect with one of our partner prop firms and get this $497 professional trading tool absolutely free!
                </p>

                {submissionStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-6 p-4 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl shadow-sm"
                  >
                    <h3 className="text-emerald-700 font-bold mb-2">🎉 Success!</h3>
                    <p className="text-emerald-600 text-sm leading-relaxed">
                      Thank you for signing up! We'll contact you within 24 hours to help you get started with your free HonPropDesk Manager v3.3.
                    </p>
                  </motion.div>
                )}

                {submissionStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-6 p-4 bg-gradient-to-r from-red-50 to-rose-50 border border-red-200 rounded-xl shadow-sm"
                  >
                    <h3 className="text-red-700 font-bold mb-2">❌ Error</h3>
                    <p className="text-red-600 text-sm leading-relaxed">
                      Something went wrong. Please try again or contact support.
                    </p>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5 text-left">
                  <div className="group">
                    <label className="block text-slate-800 text-xs sm:text-sm font-bold mb-2 sm:mb-3 group-focus-within:text-blue-600 transition-colors">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-3 sm:py-4 bg-white/85 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all duration-200 shadow-sm hover:shadow-md text-sm sm:text-base"
                      placeholder="Enter your full name"
                      disabled={submissionStatus === 'submitting'}
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-2 font-semibold">{errors.name}</p>}
                  </div>

                  <div className="group">
                    <label className="block text-slate-800 text-xs sm:text-sm font-bold mb-2 sm:mb-3 group-focus-within:text-blue-600 transition-colors">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-3 sm:py-4 bg-white/85 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all duration-200 shadow-sm hover:shadow-md text-sm sm:text-base"
                      placeholder="Enter your email"
                      disabled={submissionStatus === 'submitting'}
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-2 font-semibold">{errors.email}</p>}
                  </div>

                  <div className="group">
                    <label className="block text-slate-800 text-xs sm:text-sm font-bold mb-2 sm:mb-3 group-focus-within:text-blue-600 transition-colors">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 sm:px-4 py-3 sm:py-4 bg-white/85 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all duration-200 shadow-sm hover:shadow-md text-sm sm:text-base"
                      placeholder="Enter your phone number"
                      disabled={submissionStatus === 'submitting'}
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-2 font-semibold">{errors.phone}</p>}
                  </div>

                  <div className="group">
                    <label className="block text-slate-800 text-xs sm:text-sm font-bold mb-2 sm:mb-3 group-focus-within:text-blue-600 transition-colors">
                      Preferred Prop Firm *
                    </label>
                    <select
                      name="broker"
                      value={formData.broker}
                      onChange={handleChange}
                      className="w-full px-4 py-4 bg-white/80 border-2 border-slate-200 rounded-xl text-slate-800 focus:border-blue-400 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all duration-200 shadow-sm hover:shadow-md"
                      disabled={submissionStatus === 'submitting'}
                    >
                      <option value="" className="bg-white text-slate-400">
                        Select your prop firm
                      </option>
                      {brokers.map((broker) => (
                        <option
                          key={broker.value}
                          value={broker.value}
                          className="bg-white text-slate-800 py-2"
                        >
                          {broker.label}
                        </option>
                      ))}
                    </select>
                    {errors.broker && <p className="text-red-500 text-sm mt-2 font-semibold">{errors.broker}</p>}
                  </div>

                  {/* Conditional Custom Prop Firm Input */}
                  {formData.broker === 'Other' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="group"
                    >
                      <label className="block text-slate-800 text-xs sm:text-sm font-bold mb-2 sm:mb-3 group-focus-within:text-blue-600 transition-colors">
                        Specify Your Prop Firm *
                      </label>
                      <input
                        type="text"
                        name="customPropFirm"
                        value={formData.customPropFirm}
                        onChange={handleChange}
                        className="w-full px-3 sm:px-4 py-3 sm:py-4 bg-white/85 border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:bg-white transition-all duration-200 shadow-sm hover:shadow-md text-sm sm:text-base"
                        placeholder="Enter your prop firm name"
                        disabled={submissionStatus === 'submitting'}
                      />
                      {errors.customPropFirm && <p className="text-red-500 text-sm mt-2 font-semibold">{errors.customPropFirm}</p>}
                    </motion.div>
                  )}

                  <motion.button
                    type="submit"
                    disabled={submissionStatus === 'submitting'}
                    whileHover={{ scale: submissionStatus === 'submitting' ? 1 : 1.02 }}
                    whileTap={{ scale: submissionStatus === 'submitting' ? 1 : 0.98 }}
                    className={`w-full mt-4 sm:mt-6 py-3 sm:py-4 px-4 sm:px-8 rounded-xl font-bold text-sm sm:text-base transition-all duration-200 shadow-lg ${
                      submissionStatus === 'submitting'
                        ? 'bg-slate-400 cursor-not-allowed text-slate-600'
                        : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 hover:shadow-xl hover:shadow-blue-500/25 transform hover:-translate-y-0.5'
                    }`}
                  >
                    {submissionStatus === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2 sm:gap-3">
                        <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                        <span className="text-sm sm:text-base">Submitting...</span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2 sm:gap-3">
                        <span className="text-base sm:text-lg">🚀</span>
                        <span className="text-sm sm:text-base">Get HonPropDesk Manager FREE</span>
                      </span>
                    )}
                  </motion.button>
                </form>

                <div className="mt-8 text-center">
                  <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-slate-600 font-medium">
                    <span className="flex items-center gap-2">
                      <span className="text-emerald-500">🔒</span>
                      Secure
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-blue-500">📞</span>
                      24/7 Support
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="text-purple-500">💻</span>
                      Professional
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </>
  );
};

export default HonPropDesk;